import type { FC, PropsWithChildren } from "react";
import { Navigate, Outlet, Route, useOutletContext } from "react-router-dom";
import {
  canAccessStatistics,
  isAgentWithPackagesAccess,
  isAgentWithProductsAndProviderAccess,
  isAgentWithProvidersProductsAndDictionariesAccess,
  type UserContext,
} from "../auth/auth";
import PageTitle from "../misc/PageTitle";
import AdminPage from "./AdminPage";
import AgentsList from "./agents/AgentsList";
import EditAgentPage from "./agents/EditAgentPage";
import NewAgentPage from "./agents/NewAgentPage";
import AutomationConfig from "./automation/AutomationConfig";
import Dictionaries from "./dictionaries/Dictionaries";
import GlobalSettingsPage from "./globalSettings/GlobalSettingsPage";
import EditRewardPage from "./loyalty-rewards/EditRewardPage";
import NewRewardPage from "./loyalty-rewards/NewRewardPage";
import RewardsList from "./loyalty-rewards/RewardsList";
import LoyaltyPage from "./loyalty/LoyaltyPage";
import EditPackagePage from "./packages/EditPackagePage";
import NewPackagePage from "./packages/NewPackagePage";
import PackagesList from "./packages/PackagesList";
import EditProductPage from "./products/EditProductPage";
import NewProductPage from "./products/NewProductPage";
import ProductsList from "./products/ProductsList";
import ReadOnlyProductPage from "./products/ReadOnlyProductPage";
import ConvertOptionPrices from "./products/editProduct/ConvertOptionPrices";
import MultiOptionEdit from "./products/editProduct/MultiOptionEdit";
import ProductOptionsOrder from "./products/editProduct/ProductOptionsOrder";
import SingleOptionEdit from "./products/editProduct/SingleOptionEdit";
import { ProductMap } from "./products/map/ProductMapDialog";
import EditProviderPage from "./providers/EditProviderPage";
import NewProviderPage from "./providers/NewProviderPage";
import ProvidersList from "./providers/ProvidersList";
import ReadOnlyProviderPage from "./providers/ReadOnlyProviderPage";
import AgentCallsAdminPage from "./agentCalls/AgentCallsAdminPage";
import CustomerNotesAdminPage from "./customerNotes/CustomerNotesAdminPage";
import ClientDetailReportPage from "./statistics/ClientDetailReportPage";
import ClientsComparisonReportPage from "./statistics/ClientsComparisonReportPage";
import ProviderReportPage from "./statistics/ProviderReportPage";
import RaportyGallery from "./statistics/RaportyGallery";
import SellerActivityReportPage from "./statistics/SellerActivityReportPage";
import SellerOrdersReportPage from "./statistics/SellerOrdersReportPage";
import StatisticsLayout from "./statistics/StatisticsLayout";
import type { ChangeFilters, Filters } from "./useAdminFilters";

const OutletWithParentContext: FC = () => {
  const parentContext = useOutletContext();
  return <Outlet context={parentContext} />;
};

interface RequireProvidersAndProductsAccessProps {
  readonly context: UserContext | null;
}

const RequireProvidersAndProductsAccess: FC<PropsWithChildren<RequireProvidersAndProductsAccessProps>> = ({
  context,
  children,
}) => {
  if (context?.role === "ADMIN" || isAgentWithProductsAndProviderAccess(context)) {
    return children;
  }
  return <Navigate to="/not-found" replace />;
};

interface RequireDictionariesAccessProps {
  readonly context: UserContext | null;
}

const RequireDictionariesAccess: FC<PropsWithChildren<RequireDictionariesAccessProps>> = ({ context, children }) => {
  if (context?.role === "ADMIN" || isAgentWithProvidersProductsAndDictionariesAccess(context)) {
    return children;
  }
  return <Navigate to="/not-found" replace />;
};

interface RequirePackagesAccessProps {
  readonly context: UserContext | null;
}

const RequirePackagesAccess: FC<PropsWithChildren<RequirePackagesAccessProps>> = ({ context, children }) => {
  if (context?.role === "ADMIN" || isAgentWithPackagesAccess(context)) {
    return children;
  }
  return <Navigate to="/not-found" replace />;
};

interface RequireStatisticsAccessProps {
  readonly context: UserContext | null;
}

const RequireStatisticsAccess: FC<PropsWithChildren<RequireStatisticsAccessProps>> = ({ context, children }) => {
  if (canAccessStatistics(context)) {
    return children;
  }
  return <Navigate to="/not-found" replace />;
};

function adminIndexPath(context: UserContext | null): string {
  if (context?.role === "ADMIN") {
    return "agents";
  }
  if (!isAgentWithProductsAndProviderAccess(context) && canAccessStatistics(context)) {
    return "statistics";
  }
  return "products";
}

interface AdminRoutesProps {
  readonly role: UserContext["role"] | null;
  readonly context: UserContext | null;
  readonly filters: Filters;
  readonly onFiltersChange: ChangeFilters;
}

export const adminRoutes = ({ context, filters, onFiltersChange }: AdminRoutesProps) => {
  const role = context?.role;
  const canViewProductsAndProviders = role === "ADMIN" || isAgentWithProductsAndProviderAccess(context);
  const canEditProvidersAndProducts = role === "ADMIN" || isAgentWithProvidersProductsAndDictionariesAccess(context);

  return (
    <Route
      path="admin"
      element={
        <>
          <PageTitle title="admin" />
          <AdminPage filters={filters} onFiltersChange={onFiltersChange} />
        </>
      }
    >
      <Route index element={<Navigate to={adminIndexPath(context)} />} />
      <Route path="agents">
        <Route index element={<AgentsList />} />
        <Route path="new" element={<NewAgentPage />} />
        <Route path=":agentId" element={<EditAgentPage />} />
      </Route>
      <Route
        path="providers"
        element={
          <RequireProvidersAndProductsAccess context={context}>
            <OutletWithParentContext />
          </RequireProvidersAndProductsAccess>
        }
      >
        <Route index element={<ProvidersList />} />
        <Route
          path="new"
          element={canEditProvidersAndProducts ? <NewProviderPage /> : <Navigate to="/not-found" replace />}
        />
        <Route
          path=":providerId"
          element={
            canViewProductsAndProviders ? (
              canEditProvidersAndProducts ? (
                <EditProviderPage />
              ) : (
                <ReadOnlyProviderPage />
              )
            ) : (
              <Navigate to="/not-found" replace />
            )
          }
        />
      </Route>
      <Route
        path="products"
        element={
          <RequireProvidersAndProductsAccess context={context}>
            <OutletWithParentContext />
          </RequireProvidersAndProductsAccess>
        }
      >
        <Route index element={<ProductsList />} />
        <Route
          path="new"
          element={canEditProvidersAndProducts ? <NewProductPage /> : <Navigate to="/not-found" replace />}
        />
        <Route
          path=":productId"
          element={
            canViewProductsAndProviders ? (
              canEditProvidersAndProducts ? (
                <EditProductPage />
              ) : (
                <ReadOnlyProductPage />
              )
            ) : (
              <Navigate to="/not-found" replace />
            )
          }
        >
          {canEditProvidersAndProducts ? (
            <>
              <Route index element={<Navigate to="edit-single" replace />} />
              <Route path="edit-single" element={<SingleOptionEdit />} />
              <Route path="edit-many" element={<MultiOptionEdit />} />
              <Route path="convert" element={<ConvertOptionPrices />} />
              <Route path="order" element={<ProductOptionsOrder />} />
            </>
          ) : null}
        </Route>
      </Route>
      <Route
        path="packages"
        element={
          <RequirePackagesAccess context={context}>
            <OutletWithParentContext />
          </RequirePackagesAccess>
        }
      >
        <Route index element={<PackagesList />} />
        <Route path="new" element={<NewPackagePage />} />
        <Route path=":packageId" element={<EditPackagePage />} />
      </Route>
      <Route path="automation" element={<AutomationConfig />} />
      <Route path="global-settings" element={<GlobalSettingsPage />} />
      <Route
        path="dictionaries"
        element={
          <RequireDictionariesAccess context={context}>
            <Dictionaries />
          </RequireDictionariesAccess>
        }
      />
      <Route path="loyalty" element={<LoyaltyPage />} />
      <Route path="rewards">
        <Route index element={<RewardsList />} />
        <Route path="new" element={<NewRewardPage />} />
        <Route path=":rewardId" element={<EditRewardPage />} />
      </Route>
      <Route path="map" element={<ProductMap />} />
      <Route path="customer-notes" element={<CustomerNotesAdminPage />} />
      <Route path="phone-calls" element={<AgentCallsAdminPage />} />
      <Route
        path="statistics"
        element={
          <RequireStatisticsAccess context={context}>
            <StatisticsLayout />
          </RequireStatisticsAccess>
        }
      >
        <Route index element={<RaportyGallery />} />
        <Route path="sellers-orders" element={<SellerOrdersReportPage />} />
        <Route path="sellers-activity" element={<SellerActivityReportPage />} />
        <Route path="customer-comparison" element={<ClientsComparisonReportPage />} />
        <Route path="customer-details" element={<ClientDetailReportPage />} />
        <Route path="providers" element={<ProviderReportPage />} />
      </Route>
    </Route>
  );
};
